import { PodTypes } from '@awaze/design-system.components.pod'
import { Stack } from '@awaze/design-system.components.stack'
import { Headline } from '@awaze/design-system.components.text'
import {
  GridPodsModule,
  GridPodsModuleProps,
} from '@awaze/design-system.compositions.grid-pods-module'
import {
  ResponsivePropValue,
  Spacing,
  breakpoints,
  handleResponsiveCssValues,
} from '@awaze/design-system.utils.react'
import { JSX, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { SECTION_PADDING } from '@/components/section-composition/section'
import { usePodsShowCta } from 'src/lib/optimizely/experiments/use-pods-show-cta'
import { podsModuleDatalayer } from 'src/utils/dataLayer/podsModuleDatalayer'
import { useObserverDataLayer } from 'src/utils/dataLayer/useObserverDatalayer'
import styled from 'styled-components'

export interface PodsModuleItems extends PodTypes {
  id: string
  indexOnPage: string
  imageName?: string
}

export interface PodsModuleProps {
  headline?: string
  items?: PodsModuleItems[]
  variant?: GridPodsModuleProps['variant']
  mobileCarouselMode?: boolean
}

export const PodsModule = (props: PodsModuleProps): JSX.Element => {
  const { headline, items, variant, mobileCarouselMode } = props
  const { formatMessage } = useIntl()

  const podsHasLink = useMemo(() => items?.some((item) => item.link), [items])

  const showCta = usePodsShowCta(podsHasLink)

  const h2Items = items
    ?.map((podModuleItem) => {
      podModuleItem.titleAs = 'h2'

      if (showCta && podModuleItem.link) {
        podModuleItem.displayButton = true
        podModuleItem.buttonText = formatMessage({
          id: 'pods.ctaText',
          defaultMessage: 'View more',
        })
      }

      return podModuleItem
    })
    .filter((podModuleItem) => !podModuleItem?.image?.includes('empty'))

  const HeadlineContent = (
    <>
      {headline && (
        <Headline
          sizes={[200, 200, 200]}
          hasBorder
          as={'h2'}
          marginBottom={[32, 32, 56, 56]}
        >
          {headline}
        </Headline>
      )}
    </>
  )

  const { elementRef } = useObserverDataLayer(podsModuleDatalayer(items), {
    clickEventName: 'offer-click',
    clickSelector: '[data-ga^="marketing-pod-"]',
  })

  if (!h2Items || h2Items.length === 0) {
    return <></>
  }

  return (
    <div ref={elementRef}>
      <PodsModuleWrapper mobileCarouselMode={mobileCarouselMode}>
        {headline &&
          (h2Items ? (
            <StackStyled mobileCarouselMode={mobileCarouselMode}>
              {HeadlineContent}
            </StackStyled>
          ) : (
            HeadlineContent
          ))}
        <GridPodsModule
          variant={variant ?? 'without-bg'}
          items={h2Items}
          mobileCarouselMode={mobileCarouselMode}
        />
      </PodsModuleWrapper>
    </div>
  )
}

const PodsModuleWrapper = styled.div<{ mobileCarouselMode: boolean }>`
  ${({ mobileCarouselMode }) =>
    mobileCarouselMode &&
    `
      @media (max-width: ${breakpoints[0]}px) {
        margin-right: -${SECTION_PADDING}px;
        .reel > a:last-of-type {
          margin-right: ${SECTION_PADDING}px;
        }
      }
    `}
`

const StackStyled = styled(Stack)<{
  marginBottom?: ResponsivePropValue<Spacing>
  mobileCarouselMode: boolean
}>`
  @media (max-width: ${breakpoints[0]}px) {
    width: 100%;
    max-width: 1200px;
    padding: ${({ mobileCarouselMode }) =>
      mobileCarouselMode ? '0 24px' : '0'};
    ${({ marginBottom }) =>
      marginBottom
        ? handleResponsiveCssValues({
            cssProperty: 'margin-bottom',
            value: marginBottom,
          })
        : 0};
  }
`
